/**
 * Pods Tab Component
 *
 * Matches original HTML structure: .pods-page-container, .pods-filter-bar, .pod-card, etc.
 */

import { useState } from 'preact/hooks';
import {
  groups,
  selectedGroup,
  selectGroup,
  createGroup,
  joinGroup,
} from '@/stores/groups';
import { toast, openModal } from '@/stores/ui';

export function PodsTab() {
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('oldest');
  const [privacyFilter, setPrivacyFilter] = useState('all');

  // Filter and sort groups
  const filteredGroups = groups.value
    .filter((g) => {
      if (searchQuery && !g.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (privacyFilter === 'public' && g.isPrivate) return false;
      if (privacyFilter === 'private' && !g.isPrivate) return false;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        case 'oldest':
          return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
        case 'most-members':
          return (b.memberCount || 0) - (a.memberCount || 0);
        default:
          return 0;
      }
    });

  return (
    <div class="pods-page-container">
      <div class="pods-header">
        <div class="pods-actions-top">
          <button class="btn btn-primary" id="create-pod-btn" onClick={() => setShowCreate(true)}>
            + Create Pod
          </button>
          <button class="btn btn-secondary" onClick={() => setShowJoin(true)}>
            Join with Code
          </button>
        </div>
      </div>

      {/* Main filter bar */}
      <div class="pods-filter-bar">
        <div class="search-container">
          <input
            type="text"
            placeholder="Search pods..."
            id="pods-search-input"
            class="pods-search-input"
            value={searchQuery}
            onInput={(e) => setSearchQuery((e.target as HTMLInputElement).value)}
          />
          <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        </div>

        <div class="filter-container">
          <div class="filter-item">
            <label for="pods-sort">Sort By</label>
            <select
              id="pods-sort"
              value={sortBy}
              onChange={(e) => setSortBy((e.target as HTMLSelectElement).value)}
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="most-members">Most Members</option>
              <option value="recently-active">Recently Active</option>
            </select>
          </div>

          <div class="filter-item">
            <label for="pods-privacy">Privacy</label>
            <select
              id="pods-privacy"
              value={privacyFilter}
              onChange={(e) => setPrivacyFilter((e.target as HTMLSelectElement).value)}
            >
              <option value="all">All</option>
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          </div>
        </div>
      </div>

      {/* Create Pod Modal */}
      {showCreate && (
        <CreatePodModal onClose={() => setShowCreate(false)} />
      )}

      {/* Join Pod Modal */}
      {showJoin && (
        <JoinPodModal onClose={() => setShowJoin(false)} />
      )}

      {/* Pods Grid */}
      <div class="pods-grid-container">
        {filteredGroups.length === 0 ? (
          <div class="pods-empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 00-3-3.87" />
              <path d="M16 3.13a4 4 0 010 7.75" />
            </svg>
            <h2>No pods found</h2>
            <p>Create a new pod or join an existing one with an invite code</p>
          </div>
        ) : (
          <div class="pods-grid" id="pods-grid">
            {filteredGroups.map((group) => (
              <PodCard
                key={group.id}
                group={group}
                isSelected={selectedGroup.value?.id === group.id}
                onSelect={() => selectGroup(group.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface PodCardProps {
  group: {
    id: string;
    name: string;
    description: string | null;
    imageUrl: string | null;
    memberCount: number;
    isAdmin: boolean;
    isPrivate: boolean;
    cycleFrequency: string;
    currentMonth: string;
    currentYear: number;
    createdAt: string;
  };
  isSelected: boolean;
  onSelect: () => void;
}

function PodCard({ group, isSelected, onSelect }: PodCardProps) {
  const imageUrl = group.imageUrl || '/images/logo-transparent_nobuffer_crop_square_padded.png';
  const isPublic = !group.isPrivate;

  return (
    <div
      class={`pod-card ${isSelected ? 'selected' : ''}`}
      data-pod-id={group.id}
      onClick={onSelect}
    >
      {/* Pod image container - matches original structure */}
      <div class="pod-image-container">
        <img
          src={imageUrl}
          alt={group.name}
          class="pod-image"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/images/logo-transparent_nobuffer_crop_square_padded.png';
          }}
        />
        <div class="pod-privacy-badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            {isPublic ? (
              <>
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </>
            ) : (
              <>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </>
            )}
          </svg>
          {isPublic ? 'Public' : 'Private'}
        </div>
        {group.isAdmin && (
          <div class="owner-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
            </svg>
            Owner
          </div>
        )}
      </div>

      {/* Pod content - matches original structure */}
      <div class="pod-content">
        <h3 class="pod-name">{group.name}</h3>
        <p class="pod-description">{group.description || 'No description'}</p>
        <div class="pod-meta">
          <span class="pod-members">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 00-3-3.87"></path>
              <path d="M16 3.13a4 4 0 010 7.75"></path>
            </svg>
            {group.memberCount} member{group.memberCount !== 1 ? 's' : ''}
          </span>
          <span class="pod-cycle-badge">{group.cycleFrequency}</span>
        </div>
        <div class="pod-actions">
          {group.isAdmin ? (
            <button
              class="btn view-pod-btn"
              onClick={(e) => {
                e.stopPropagation();
                openModal('PodSettings', { groupId: group.id });
              }}
            >
              Manage Pod
            </button>
          ) : (
            <button
              class="btn view-pod-btn"
              onClick={(e) => {
                e.stopPropagation();
                onSelect();
              }}
            >
              View Pod
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CreatePodModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSubmitting(true);
    const group = await createGroup({
      name: name.trim(),
      description: description.trim() || undefined,
    });

    if (group) {
      toast.success(`Created ${group.name}!`);
      onClose();
    } else {
      toast.error('Failed to create pod');
    }
    setIsSubmitting(false);
  };

  return (
    <div class="modal-overlay" id="create-group-modal" style={{ display: 'flex' }} onClick={onClose}>
      <div class="modal-content" onClick={(e) => e.stopPropagation()}>
        <div class="modal-header">
          <h2>Create a Pod</h2>
          <button class="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div class="form-group">
            <label for="new-group-name">Pod Name</label>
            <input
              id="new-group-name"
              type="text"
              class="form-input"
              value={name}
              onInput={(e) => setName((e.target as HTMLInputElement).value)}
              placeholder="e.g., Music Lovers"
              required
              maxLength={50}
            />
          </div>

          <div class="form-group">
            <label for="new-group-description">Description (optional)</label>
            <textarea
              id="new-group-description"
              class="form-textarea"
              value={description}
              onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
              placeholder="What's this pod about?"
              maxLength={200}
            />
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-light" id="cancel-create-group" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              id="confirm-create-group"
              disabled={isSubmitting || !name.trim()}
            >
              {isSubmitting ? 'Creating...' : 'Create Pod'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function JoinPodModal({ onClose }: { onClose: () => void }) {
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!code.trim()) return;

    setIsSubmitting(true);
    const group = await joinGroup(code.trim());

    if (group) {
      toast.success(`Joined ${group.name}!`);
      onClose();
    } else {
      toast.error('Invalid invite code');
    }
    setIsSubmitting(false);
  };

  return (
    <div class="modal-overlay" onClick={onClose}>
      <div class="modal-content" onClick={(e) => e.stopPropagation()}>
        <div class="modal-header">
          <h2>Join a Pod</h2>
          <button class="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div class="form-group">
            <label for="invite-code">Invite Code</label>
            <input
              id="invite-code"
              type="text"
              class="form-input"
              value={code}
              onInput={(e) => setCode((e.target as HTMLInputElement).value)}
              placeholder="Enter invite code"
              required
            />
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-light" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              disabled={isSubmitting || !code.trim()}
            >
              {isSubmitting ? 'Joining...' : 'Join Pod'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
